import React from 'react';
import { 
  Box, Paper, Typography, Divider,
  Card, CardContent, CardHeader
} from '@mui/material';
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';

const AnalyticsPanel = ({ tripData, tripsByMonth }) => {
  // Calculate summary statistics
  const totalDistance = tripData.reduce((sum, trip) => sum + (trip.totalKm || 0), 0);
  const totalFuel = tripData.reduce((sum, trip) => sum + (trip.totalFuel || 0), 0);
  const totalEmptyDistance = tripData.reduce((sum, trip) => sum + (trip.emptyKm || 0), 0);
  const overallEfficiency = totalDistance / totalFuel;
  
  // Calculate trips by type
  const tripTypes = tripData.reduce((acc, trip) => {
    const type = trip.type;
    if (!acc[type]) {
      acc[type] = 0;
    }
    acc[type]++;
    return acc;
  }, {});
  
  const tripTypeData = Object.entries(tripTypes)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));
  
  // Prepare monthly data for charts
  const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const monthlyData = Object.entries(tripsByMonth)
    .map(([month, trips]) => {
      const totalKm = trips.reduce((sum, trip) => sum + (trip.totalKm || 0), 0);
      const totalFuel = trips.reduce((sum, trip) => sum + (trip.totalFuel || 0), 0);
      const efficiency = totalFuel > 0 ? totalKm / totalFuel : 0;
      
      return {
        month,
        monthIndex: monthOrder.indexOf(month),
        totalKm,
        totalFuel,
        efficiency,
        tripCount: trips.length
      };
    })
    .sort((a, b) => a.monthIndex - b.monthIndex);
  
  // Prepare data for top destinations
  const destinationCounts = {};
  tripData.forEach(trip => {
    if (trip.segments) {
      trip.segments.forEach(segment => {
        if (!destinationCounts[segment.to]) {
          destinationCounts[segment.to] = 0;
        }
        destinationCounts[segment.to]++;
      });
    }
  });
  
  const topDestinations = Object.entries(destinationCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, value]) => ({ name, value }));
  
  // Color scheme for charts
  const COLORS = ['#3f51b5', '#f50057', '#00bcd4', '#ff9800', '#4caf50', '#9c27b0'];

  // Chart container style
  const chartContainerStyle = {
    width: '100%', 
    marginBottom: '40px',
    display: 'block'
  };
  
  return (
    <Box sx={{ flexGrow: 1, width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Trip Analytics Dashboard
      </Typography>
      
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', sm: '23%' } }}>
          <Typography variant="h4" color="primary">
            {Math.round(totalDistance).toLocaleString()} km
          </Typography>
          <Typography variant="body1">Total Distance</Typography>
        </Paper>
        
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', sm: '23%' } }}>
          <Typography variant="h4" color="error">
            {Math.round(totalFuel).toLocaleString()} L
          </Typography>
          <Typography variant="body1">Total Fuel Used</Typography>
        </Paper>
        
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', sm: '23%' } }}>
          <Typography variant="h4" color="success.main">
            {overallEfficiency.toFixed(2)} km/L
          </Typography>
          <Typography variant="body1">Fuel Efficiency</Typography>
        </Paper>
        
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', sm: '23%' } }}>
          <Typography variant="h4" color="warning.main">
            {((totalEmptyDistance / totalDistance) * 100).toFixed(1)}%
          </Typography>
          <Typography variant="body1">Empty Running</Typography>
        </Paper>
      </Box>
      
      {/* Monthly Distance Chart */}
      <Box sx={chartContainerStyle}>
        <Card>
          <CardHeader title="Monthly Distance Traveled" />
          <Divider />
          <CardContent sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${Math.round(value).toLocaleString()} km`} />
                <Legend />
                <Bar dataKey="totalKm" name="Total KM" fill="#3f51b5" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
      
      {/* Monthly Fuel Consumption Chart */}
      <Box sx={chartContainerStyle}>
        <Card>
          <CardHeader title="Monthly Fuel Consumption" />
          <Divider />
          <CardContent sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${Math.round(value).toLocaleString()} L`} />
                <Legend />
                <Bar dataKey="totalFuel" name="Total Fuel (L)" fill="#f50057" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
      
      {/* Trip Types Pie Chart */}
      <Box sx={chartContainerStyle}>
        <Card>
          <CardHeader title="Trip Types Distribution" />
          <Divider />
          <CardContent sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tripTypeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {tripTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} trips`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
      
      {/* Top Destinations Chart */}
      <Box sx={chartContainerStyle}>
        <Card>
          <CardHeader title="Top Destinations" />
          <Divider />
          <CardContent sx={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topDestinations}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" name="Visit Count" fill="#00bcd4" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
      
      {/* Efficiency Trend Chart */}
      <Box sx={chartContainerStyle}>
        <Card>
          <CardHeader title="Monthly Efficiency Trend" />
          <Divider />
          <CardContent sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${value.toFixed(2)} km/L`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="efficiency" 
                  name="Fuel Efficiency (km/L)" 
                  stroke="#4caf50" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AnalyticsPanel;
